/* eslint-disable */
import axios from "axios";
import { showAccessDenied } from "@/access-denied";

export const base = process.env.VUE_APP_BASE_URL_HMS;
export const hms     = base + "/index.php";
export const uploads = base + "/uploads/";

// Bounce to /login when the auth token is missing or the server says the
// session died. Handles the "signed out in another tab" cross-tab race that
// otherwise produces the literal Authorization: Bearer null header.
function bounceToLogin(reason) {
    try {
        sessionStorage.removeItem("isLoggedIn");
        localStorage.removeItem("authToken");
    } catch (_) {}
    // Only push if we're not already at /login (avoid a router loop)
    if (typeof window !== "undefined" && !location.hash.startsWith("#/login")) {
        location.hash = "#/login?" + (reason ? "reason=" + reason : "timeout=1");
    }
}

// Global 403-with-category-code interceptor.
// The backend returns {status:403, success:false, message:"Access denied...",
//   data:{category_code, required, granted}} when RBAC denies a data category.
// Instead of every calling site handling it as "empty state", we push it
// into a shared reactive store and App.vue renders a friendly banner.
// Detect a session-expired response by any means the backend uses.
function isSessionExpired(status, body) {
    if (status === 401) return true;
    if (!body) return false;
    if (body.status === 401) return true;
    if (body.success === false && typeof body.message === "string") {
        const m = body.message.toLowerCase();
        if (m.indexOf("session expired") !== -1) return true;
        if (m.indexOf("please log in again") !== -1) return true;
        if (m.indexOf("invalid token") !== -1) return true;
        if (m.indexOf("token expired") !== -1) return true;
    }
    return false;
}

axios.interceptors.response.use(
    (r) => {
        // Backend often returns HTTP 200 with success:false for session-expired.
        // Bounce anyway so the user sees the login screen instead of a broken UI.
        try {
            if (isSessionExpired(r && r.status, r && r.data)) {
                bounceToLogin("expired");
            }
        } catch (_) { /* ignore */ }
        return r;
    },
    (err) => {
        try {
            const status = err && err.response && err.response.status;
            const data   = err && err.response && err.response.data;
            if (isSessionExpired(status, data)) { bounceToLogin("expired"); }
            if (data && data.data && data.data.category_code && data.success === false) {
                showAccessDenied({
                    category_code: data.data.category_code,
                    required:      data.data.required,
                    granted:       data.data.granted,
                    message:       data.message,
                });
            }
        } catch (_) { /* ignore */ }
        return Promise.reject(err);
    }
);

// Endpoints callable BEFORE authentication — they must not be blocked by the
// "no token" guard because they run on the /login screen itself.
const PUBLIC_FNS = new Set([
    "loginUser", "getPublicBrand", "getPublicI18n", "getI18nStrings",
    "requestPasswordReset", "resetPasswordWithToken", "checkOrgSignupStatus",
    "submitOrgSignup", "mfaVerify", "getMfaTicketStatus",
    "breakGlassLogin", "beginBreakGlass"
]);

// Authenticated POST — sends Bearer token + JSON body. If no token exists and
// the caller is a POST-login endpoint, redirect to /login instead of firing
// the request (that avoids the literal "Bearer null" header and its spurious 401).
export const executeJson = (params, url) => {
    const tokenStr = localStorage.getItem("authToken");
    const fn       = params && params.function;
    const isPublic = fn && PUBLIC_FNS.has(fn);

    if (!isPublic && (!tokenStr || tokenStr === "null" || tokenStr === "undefined")) {
        bounceToLogin("no_token");
        return Promise.reject(new Error("No auth token — redirecting to login"));
    }

    const headers = { "Content-Type": "application/json" };
    if (tokenStr && tokenStr !== "null" && tokenStr !== "undefined") {
        headers["Authorization"] = "Bearer " + tokenStr;
    }
    return axios.post(url, params, { headers });
};

// Unauthenticated POST — for public endpoints (queue display, symptom checker)
export const execute2 = (params, url) => {
    return axios.post(url, params);
};

// Patient Portal — separate Bearer token in sessionStorage (key: portalToken)
export const executePortalJson = (params, url) => {
    const tokenStr = sessionStorage.getItem("portalToken");
    return axios.post(url, params, {
        headers: {
            "Authorization": `Bearer ${tokenStr}`,
            "Content-Type": "application/json"
        }
    });
};
